"use client"

import { motion } from "framer-motion"
import { Button } from "@/components/ui/button"
import {
  RefreshCw,
  Users,
  TrendingUp,
  GraduationCap,
  Clock,
  Baby,
  Building2,
} from "@/lib/icons"
import { formatNumber, formatPercentage } from "@/lib/utils"

/**
 * Header del panel administrativo: título + 5 KPIs ejecutivos + campus.
 * Es la ÚNICA fuente de totales globales del dashboard (rediseño 2026-06-09).
 * El filtro "Etapa del prospecto" se eliminó: esa exploración vive en el
 * Kanban y la tabla de leads; el dashboard responde preguntas, no filtra.
 */
export function DashboardFilters({
  adminDisplayName,
  summary,
  enrolledCount,
  totalNinos = 0,
  refreshing,
  onRefresh,
}) {
  const stats = [
    {
      label: "Prospectos",
      value: formatNumber(summary.total),
      color: "#7a1b33",
      icon: Users,
      helper: summary.todayNew > 0 ? `+${summary.todayNew} hoy` : "del mes",
    },
    {
      label: "Niños",
      value: formatNumber(totalNinos),
      color: "#ec4899",
      icon: Baby,
      helper: summary.total > 0 ? `${(totalNinos / Math.max(summary.total, 1)).toFixed(1)} x prospecto` : "ventas reales",
    },
    {
      label: "Conversión",
      value: formatPercentage(summary.conversionRate),
      color: "#d94468",
      icon: TrendingUp,
      helper: "leads → inscritos",
    },
    {
      label: "Inscritos",
      value: formatNumber(enrolledCount),
      color: "#10b981",
      icon: GraduationCap,
      helper: "del mes",
    },
    {
      label: "Seguimiento",
      value: formatNumber(summary.followUp),
      color: "#f59e0b",
      icon: Clock,
      helper: `${formatPercentage(summary.followUpRate)} del total`,
    },
  ]

  return (
    <section className="mb-6">
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: "tween", duration: 0.5 }}
        className="relative overflow-hidden rounded-2xl border border-slate-200/60 bg-white p-5 sm:p-6 shadow-sm dark:border-slate-700/60 dark:bg-slate-900"
      >
        {/* Barra decorativa + brillo */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#7a1b33] via-[#d94468] to-[#7a1b33]" />
        <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-rose-500/5 blur-3xl" />

        {/* Título */}
        <div className="relative flex items-start justify-between gap-3">
          <div className="space-y-1">
            <p className="text-xs font-medium text-slate-400 dark:text-slate-500">
              <span className="text-rose-700 dark:text-rose-400 font-semibold">{adminDisplayName}</span>
            </p>
            <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">
              Panel Administrativo
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <span
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900/50 px-2.5 py-1 text-xs font-bold text-rose-700 dark:text-rose-300"
              title="Campus activo"
            >
              <Building2 className="h-3 w-3" />
              IT Innovation School Guadalajara
            </span>
            <Button
              onClick={onRefresh}
              size="sm"
              variant="ghost"
              className="h-9 w-9 p-0 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800"
              disabled={refreshing}
              title="Actualizar"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>

        {/* KPIs ejecutivos — única fuente de totales del dashboard */}
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
          {stats.map((s, i) => {
            const Icon = s.icon
            return (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: 0.08 + i * 0.05 }}
                className="group relative flex items-center gap-2.5 rounded-xl border border-slate-200/70 dark:border-slate-700/60 bg-white dark:bg-slate-900 px-3 py-2 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
              >
                <div
                  className="flex h-9 w-9 items-center justify-center rounded-lg flex-shrink-0 shadow-sm"
                  style={{ backgroundColor: `${s.color}18`, boxShadow: `0 2px 6px ${s.color}20` }}
                >
                  <Icon className="h-4 w-4" style={{ color: s.color }} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[9px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 truncate">
                    {s.label}
                  </p>
                  <p className="text-lg font-extrabold tabular-nums text-slate-900 dark:text-white leading-tight">
                    {s.value}
                  </p>
                  {s.helper && (
                    <p className="text-[9px] text-slate-400 dark:text-slate-500 truncate leading-tight">
                      {s.helper}
                    </p>
                  )}
                </div>
              </motion.div>
            )
          })}
        </div>
      </motion.div>
    </section>
  )
}

export default DashboardFilters
